export const KPI_UNIT_NONE_VALUE = "";
export const KPI_UNIT_OTHER_VALUE = "__other__";
export const KPI_UNIT_CUSTOM_MAX_LENGTH = 50;

const STANDARD_KPI_UNITS = [
  { key: "number", label: "Number", storedValue: "Number", aliases: [] },
  { key: "percentage", label: "Percentage", storedValue: "Percentage", aliases: ["%", "percent"] },
  { key: "gbp", label: "GBP", storedValue: "GBP", aliases: ["£", "Â£"] },
  { key: "days", label: "Days", storedValue: "Days", aliases: ["day"] },
  { key: "hours", label: "Hours", storedValue: "Hours", aliases: ["hour"] },
  { key: "minutes", label: "Minutes", storedValue: "Minutes", aliases: ["minute"] },
  { key: "customers", label: "Customers", storedValue: "Customers", aliases: ["customer"] },
  { key: "incidents", label: "Incidents", storedValue: "Incidents", aliases: ["incident"] },
  { key: "complaints", label: "Complaints", storedValue: "Complaints", aliases: ["complaint"] },
  { key: "transactions", label: "Transactions", storedValue: "Transactions", aliases: ["transaction"] },
  {
    key: "basis_points",
    label: "Basis points",
    storedValue: "Basis points",
    aliases: ["bps", "basis point"],
  },
];

export const KPI_UNIT_OPTIONS = [
  { value: KPI_UNIT_NONE_VALUE, label: "No unit" },
  ...STANDARD_KPI_UNITS.map((unit) => ({ value: unit.key, label: unit.label })),
  { value: KPI_UNIT_OTHER_VALUE, label: "Other" },
];

const standardUnitByKey = new Map(STANDARD_KPI_UNITS.map((unit) => [unit.key, unit]));

const legacyAliasToKey = (() => {
  const aliasMap = new Map();
  STANDARD_KPI_UNITS.forEach((unit) => {
    [unit.label, unit.storedValue, ...(unit.aliases || [])].forEach((alias) => {
      aliasMap.set(String(alias).trim().toLowerCase(), unit.key);
    });
  });
  return aliasMap;
})();

function normalizeUnitCandidate(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

export function createKpiUnitFieldState(rawUnit = null) {
  const initialRawValue = rawUnit === null || rawUnit === undefined ? null : String(rawUnit);
  const normalized = normalizeUnitCandidate(initialRawValue);

  if (!normalized) {
    return {
      selectedKey: KPI_UNIT_NONE_VALUE,
      customValue: "",
      initialRawValue,
      isDirty: false,
    };
  }

  const matchedKey = legacyAliasToKey.get(normalized);
  if (matchedKey) {
    return {
      selectedKey: matchedKey,
      customValue: "",
      initialRawValue,
      isDirty: false,
    };
  }

  return {
    selectedKey: KPI_UNIT_OTHER_VALUE,
    customValue: initialRawValue,
    initialRawValue,
    isDirty: false,
  };
}

export function validateKpiUnitFieldState(unitField) {
  if (!unitField || unitField.selectedKey !== KPI_UNIT_OTHER_VALUE) {
    return "";
  }

  const trimmedValue = String(unitField.customValue || "").trim();
  if (!trimmedValue) {
    return "Custom unit is required.";
  }

  if (trimmedValue.length > KPI_UNIT_CUSTOM_MAX_LENGTH) {
    return `Custom unit must be ${KPI_UNIT_CUSTOM_MAX_LENGTH} characters or fewer.`;
  }

  return "";
}

export function resolveKpiUnitApiValue(unitField) {
  if (!unitField) {
    return null;
  }

  if (!unitField.isDirty) {
    return unitField.initialRawValue;
  }

  if (unitField.selectedKey === KPI_UNIT_NONE_VALUE) {
    return null;
  }

  if (unitField.selectedKey === KPI_UNIT_OTHER_VALUE) {
    const trimmedValue = String(unitField.customValue || "").trim();
    return trimmedValue || null;
  }

  return standardUnitByKey.get(unitField.selectedKey)?.storedValue || null;
}

export function formatKpiValueWithUnit(value, unit) {
  if (value === null || value === undefined) {
    return "";
  }

  const displayValue = String(value);
  const displayUnit = unit === null || unit === undefined ? "" : String(unit).trim();
  return displayUnit ? `${displayValue} ${displayUnit}` : displayValue;
}
